import { useEffect, useState } from "react";
import { Loader2, ThumbsUp, ChevronsUp, ChevronUp, ChevronDown, ChevronsDown } from "lucide-react";
import { toast } from "sonner";
import { rankingApi } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

const OPTIONS = [
  { id: "much_more_famous", label: "Much more famous", icon: ChevronsUp, color: "#e11d48" },
  { id: "more_famous", label: "A bit more famous", icon: ChevronUp, color: "#ff6b00" },
  { id: "ok", label: "Ranking looks right", icon: ThumbsUp, color: "#0f8a5f" },
  { id: "more_rare", label: "A bit rarer", icon: ChevronDown, color: "#2563eb" },
  { id: "much_more_rare", label: "Much rarer", icon: ChevronsDown, color: "#8a2be2" },
];

export const RankingVote = ({ tuneId, onScoreChange }) => {
  const { user, login } = useAuth();
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    rankingApi.get(tuneId).then(setState).catch(() => {});
  }, [tuneId]);

  const vote = async (id) => {
    if (!user) {
      toast.error("Sign in to rate a ranking");
      login();
      return;
    }
    setBusy(id);
    try {
      const res = await rankingApi.vote(tuneId, id);
      setState(res);
      onScoreChange?.(res.fame_score);
      toast.success("Thanks — your opinion nudges this ranking");
    } catch {
      toast.error("Could not save your vote");
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="rounded-2xl border border-[#e5e0d8] bg-white p-3" data-testid="ranking-vote">
      <p className="text-xs font-extrabold uppercase tracking-wide text-[#6e655b]">
        Does this ranking feel right?
      </p>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {OPTIONS.map((o) => {
          const active = state?.my_vote === o.id;
          const Icon = o.icon;
          return (
            <button
              key={o.id}
              onClick={() => vote(o.id)}
              disabled={busy === o.id}
              className="flex items-center gap-1 rounded-full border-2 px-2.5 py-1.5 text-xs font-bold transition-transform active:scale-95 disabled:opacity-60"
              style={{
                borderColor: active ? o.color : "#e5e0d8",
                backgroundColor: active ? `${o.color}14` : "#fff",
                color: active ? o.color : "#2d2824",
              }}
              data-testid={`vote-${o.id}`}
            >
              {busy === o.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Icon className="h-3.5 w-3.5" />}
              {o.label}
              {state?.counts?.[o.id] > 0 && (
                <span className="text-[#a89e92]">{state.counts[o.id]}</span>
              )}
            </button>
          );
        })}
      </div>
      {state && (
        <p className="mt-2 text-xs font-semibold text-[#6e655b]" data-testid="ranking-adjust">
          Community adjustment{" "}
          <b style={{ color: state.community_adjust >= 0 ? "#e11d48" : "#2563eb" }}>
            {state.community_adjust > 0 ? "+" : ""}
            {state.community_adjust}
          </b>{" "}
          on a base of {state.fame_base} · {state.votes_total} vote
          {state.votes_total === 1 ? "" : "s"} (max ±{state.max_adjust})
        </p>
      )}
    </div>
  );
};
